import { useState, useEffect, useRef } from "react";

/**
 * Tracks the pixel width of a container element and updates on window resize.
 *
 * Attach the returned `containerRef` to the container's `ref` prop. On mount and
 * on every window `resize` event (debounced by 150 ms) the hook reads
 * `containerRef.current.offsetWidth` when the ref is attached, falling back to
 * `window.innerWidth` otherwise.
 *
 * @returns {{ containerRef: React.RefObject, containerWidth: number }}
 */
export const useContainerWidth = () => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);

  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      } else {
        setContainerWidth(window.innerWidth);
      }
    };

    // Update on mount
    updateContainerWidth();

    // Add resize listener with debouncing
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        updateContainerWidth();
      }, 150);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  return { containerRef, containerWidth };
};
